import React from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { PageLoadingState } from '../LoadingSpinner';
import { TRADE_STYLES } from '../../utils/categoryToTrade';
import { categoryToTradeKey } from '../../utils/categoryToTrade';
import { leadSelectSearchText } from '../../utils/leadSelectOption';
import { FALLBACK_ACTIVITY_TYPES } from '../../utils/activityIcons';
import { ymdDayEndIso, ymdDayStartIso } from '../../utils/activityDateTime';
import { useCompanyTimezone } from '../../hooks/useCompanyTimezone';
import { useHorizontalWheelScroll } from '../../hooks/useHorizontalWheelScroll';
import { leadDetailPath } from '../../utils/leadRoutes';
import { CalendarViewSwitcher } from './CalendarViewSwitcher';
import {
  activityToGridBlock,
  absenceToGridBlock,
  buildUtilizationDays,
  createDraftToGridBlock,
  mergeAdjacentAbsenceBlocks,
} from './mapActivityToBlock';
import { PENDING_CREATE_BLOCK_ID } from './plantafelTypes';
import type { PlantafelCreatePreview } from './plantafelCreatePreview';
import { PlantafelGridBody } from './PlantafelGrid';
import { PlantafelActivityTypesContext } from './plantafelBlockTooltip';
import { UtilizationFooter } from './UtilizationFooter';
import { PlantafelDetailDrawer } from './PlantafelDetailDrawer';
import { PlantafelAbsenceDrawer } from './PlantafelAbsenceDrawer';
import { useAssigneeConflictConfirm } from '../activities/useAssigneeConflictConfirm';
import { blockToAbsenceDraft, type PlantafelAbsenceDraft } from './plantafelAbsenceMapping';
import {
  addDays,
  getBoardRange,
  getMonthViewportMeta,
  getViewportStart,
  isoWeekNumber,
  localDateKey,
  navigateFocusNext,
  navigateFocusPrev,
} from './plantafelViewport';
import { PlantafelSettingsMenu } from './PlantafelSettingsMenu';
import { PlantafelToolbarFilters } from './PlantafelToolbarFilters';
import {
  DEFAULT_PLANTAFEL_DISPLAY_SETTINGS,
  normalizePlantafelDisplaySettings,
} from '../../utils/plantafelSettings';
import {
  buildPlantafelTypeFilterOptions,
  type AbsenceBlock,
  type BoardActivity,
  type BoardEmployee,
  type GridBlock,
  type PlantafelCreateDraft,
  type PlantafelViewMode,
  PLANTAFEL_ORPHAN_LEAD_ID,
  type ProjectRow,
  type PublicHoliday,
} from './plantafelTypes';
type LeadOption = {
  id: string;
  display_id: string;
  name: string;
  lead_title?: string;
  created_at?: string;
  bestellnummer?: string | null;
  site_contact_name?: string | null;
  site_address?: string | null;
};
type MemberOption = { user_id: string; email: string; display_name: string | null };
type ActivityTypeOption = { id: string; label: string; icon?: string };

/** Einheitliche Toolbar-Typografie (Tag / Woche / Monat). */
const TOOLBAR_TEXT = 'text-sm font-medium';
/** Segment-Controls auf Settings-Höhe `h-9` (wie Filter-Trigger). */
const TOOLBAR_SEGMENT_BTN = `inline-flex h-8 items-center rounded-md px-2.5 ${TOOLBAR_TEXT}`;
const TOOLBAR_SEGMENT_WRAP = 'inline-flex h-9 items-center rounded-md bg-app-surface-muted p-0.5';

type PlantafelViewProps = {
  instancePrefix: string;
  leads: LeadOption[];
  activityTypes: ActivityTypeOption[];
  instanceMembers: MemberOption[];
  onLeadCreated?: (lead: LeadOption) => void;
  filters?: {
    mitarbeiter: string[];
    projekt: string[];
    typ: string[];
    status: string[];
  };
  onFiltersChange?: (next: {
    mitarbeiter: string[];
    projekt: string[];
    typ: string[];
    status: string[];
  }) => void;
};

export function PlantafelView({
  instancePrefix,
  leads,
  activityTypes,
  instanceMembers,
  onLeadCreated,
  filters: controlledFilters,
  onFiltersChange,
}: PlantafelViewProps) {
  const timeZone = useCompanyTimezone();
  const [focusDate, setFocusDate] = React.useState(() => new Date());
  const [viewMode, setViewMode] = React.useState<PlantafelViewMode>('woche');
  const [now, setNow] = React.useState(() => new Date());
  const [isLoading, setIsLoading] = React.useState(true);
  const [employees, setEmployees] = React.useState<BoardEmployee[]>([]);
  const [activities, setActivities] = React.useState<BoardActivity[]>([]);
  const [absences, setAbsences] = React.useState<AbsenceBlock[]>([]);
  const [publicHolidays, setPublicHolidays] = React.useState<PublicHoliday[]>([]);
  const [loadedRangeKey, setLoadedRangeKey] = React.useState<string | null>(null);
  const boardRequestIdRef = React.useRef(0);
  const [groupBy, setGroupBy] = React.useState<'mitarbeiter' | 'projekt'>('mitarbeiter');
  const [collapsed, setCollapsed] = React.useState({ mitarbeiter: false, projekte: false });
  const [uncontrolledFilters, setUncontrolledFilters] = React.useState<{
    mitarbeiter: string[];
    projekt: string[];
    typ: string[];
    status: string[];
  }>({
    mitarbeiter: [],
    projekt: [],
    typ: [],
    status: [],
  });
  const filters = controlledFilters ?? uncontrolledFilters;
  const setFilters = React.useCallback(
    (
      updater: (prev: {
        mitarbeiter: string[];
        projekt: string[];
        typ: string[];
        status: string[];
      }) => {
        mitarbeiter: string[];
        projekt: string[];
        typ: string[];
        status: string[];
      },
    ) => {
      if (onFiltersChange) {
        onFiltersChange(updater(controlledFilters ?? uncontrolledFilters));
        return;
      }
      setUncontrolledFilters(updater);
    },
    [controlledFilters, onFiltersChange, uncontrolledFilters],
  );
  const [selectedBlockId, setSelectedBlockId] = React.useState<string | null>(null);
  const [drawerActivityId, setDrawerActivityId] = React.useState<string | null>(null);
  const [showCreate, setShowCreate] = React.useState(false);
  const [createDraft, setCreateDraft] = React.useState<PlantafelCreateDraft | null>(null);
  /** Was das Formular anlegen würde; überschreibt die aufgezogene Fläche (z. B. Serie = ein Balken je Tag). */
  const [createPreview, setCreatePreview] = React.useState<PlantafelCreatePreview | null>(null);
  const [absenceDrawer, setAbsenceDrawer] = React.useState<PlantafelAbsenceDraft | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [displaySettings, setDisplaySettings] = React.useState(DEFAULT_PLANTAFEL_DISPLAY_SETTINGS);
  const visibleWeekdays = displaySettings.visibleWeekdays;

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/settings/plantafel', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setDisplaySettings(normalizePlantafelDisplaySettings(data));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const monthMeta = React.useMemo(
    () => (viewMode === 'monat' ? getMonthViewportMeta(focusDate) : null),
    [focusDate, viewMode],
  );

  const viewportStart = React.useMemo(() => {
    if (monthMeta) return monthMeta.viewportStart;
    return getViewportStart(focusDate, viewMode);
  }, [focusDate, viewMode, monthMeta]);

  const monthDayCount = monthMeta?.dayCount;

  const boardRangeKey = React.useMemo(() => {
    const { from, to } = getBoardRange(viewportStart, viewMode, monthDayCount);
    return `${localDateKey(from)}_${localDateKey(to)}_${viewMode}`;
  }, [viewportStart, viewMode, monthDayCount]);

  const boardReady = loadedRangeKey === boardRangeKey && !isLoading;

  // Das Raster ist im Wochen-/Monatsmodus breiter als der Viewport — Shift+Rad scrollt quer.
  const setBoardScrollNode = useHorizontalWheelScroll<HTMLDivElement>();

  const { intercept: interceptConflict, dialog: conflictDialog } = useAssigneeConflictConfirm();

  const loadBoard = React.useCallback(async () => {
    const requestId = ++boardRequestIdRef.current;
    const rangeKey = boardRangeKey;
    setIsLoading(true);
    setError(null);
    const { from, to } = getBoardRange(viewportStart, viewMode, monthDayCount);
    try {
      const res = await fetch(
        `/api/plantafel/board?from=${encodeURIComponent(ymdDayStartIso(localDateKey(from), timeZone))}&to=${encodeURIComponent(ymdDayEndIso(localDateKey(to), timeZone))}`,
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Plantafel konnte nicht geladen werden.');
      }
      const data = await res.json();
      if (requestId !== boardRequestIdRef.current) return;
      setEmployees(Array.isArray(data.employees) ? data.employees : []);
      setActivities(Array.isArray(data.activities) ? data.activities : []);
      setAbsences(Array.isArray(data.absence_blocks) ? data.absence_blocks : []);
      setPublicHolidays(Array.isArray(data.public_holidays) ? data.public_holidays : []);
      setLoadedRangeKey(rangeKey);
    } catch (e) {
      if (requestId !== boardRequestIdRef.current) return;
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
      setEmployees([]);
      setActivities([]);
      setAbsences([]);
      setPublicHolidays([]);
      setLoadedRangeKey(rangeKey);
    } finally {
      if (requestId === boardRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [viewportStart, viewMode, monthDayCount, boardRangeKey, timeZone]);

  React.useEffect(() => {
    void loadBoard();
  }, [loadBoard]);

  React.useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(t);
  }, []);

  const publicHolidayByDate = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const h of publicHolidays) {
      const key = String(h.holiday_date).slice(0, 10);
      if (key && h.name) map.set(key, h.name);
    }
    return map;
  }, [publicHolidays]);

  const blocks = React.useMemo(() => {
    if (!boardReady) return [];
    const list: GridBlock[] = [];
    for (const a of activities) {
      const b = activityToGridBlock(a, viewportStart, viewMode, monthDayCount, visibleWeekdays, timeZone);
      if (b) list.push(b);
    }
    for (const abs of absences) {
      const b = absenceToGridBlock(abs, viewportStart, viewMode, monthDayCount, visibleWeekdays, timeZone);
      if (b) list.push(b);
    }
    const merged = mergeAdjacentAbsenceBlocks(list);
    if (showCreate && createDraft) {
      const ranges = createPreview ?? [createDraft];
      ranges.forEach((range, i) => {
        const pending = createDraftToGridBlock(
          { ...createDraft, due_at: range.due_at, end_at: range.end_at },
          viewportStart,
          viewMode,
          monthDayCount,
          'Neuer Termin',
          visibleWeekdays,
          timeZone,
        );
        if (pending) merged.push(i === 0 ? pending : { ...pending, id: `${PENDING_CREATE_BLOCK_ID}:${i}` });
      });
    }
    return merged;
  }, [
    boardReady,
    activities,
    absences,
    viewportStart,
    viewMode,
    monthDayCount,
    showCreate,
    createDraft,
    createPreview,
    visibleWeekdays,
    timeZone,
  ]);

  const utilizationDays = React.useMemo(
    () =>
      boardReady
        ? buildUtilizationDays(viewportStart, viewMode, employees, blocks, monthDayCount)
        : [],
    [boardReady, viewportStart, viewMode, employees, blocks, monthDayCount],
  );

  const projects = React.useMemo(() => {
    const byLead = new Map<string, ProjectRow>();
    const orphanAssignees = new Set<string>();
    let hasOrphan = false;

    const addAssignees = (act: BoardActivity, target: Set<string>) => {
      if (act.primary_employee_id) target.add(act.primary_employee_id);
      for (const e of act.assignee_employees) target.add(e.id);
    };

    for (const a of activities) {
      if (!a.lead_id) {
        hasOrphan = true;
        addAssignees(a, orphanAssignees);
        continue;
      }
      let row = byLead.get(a.lead_id);
      if (!row) {
        const customer = String(a.lead_name ?? '').trim() || '—';
        const title = String(a.lead_title ?? a.title ?? '').trim() || 'Projekt';
        row = {
          key: `${customer} · ${title}`,
          leadId: a.lead_id,
          title,
          customer,
          trade: categoryToTradeKey(a.lead_category),
          assigneeEmployeeIds: [],
        };
        byLead.set(a.lead_id, row);
      }
      row.assigneeEmployeeIds = [
        ...new Set([
          ...row.assigneeEmployeeIds,
          ...(a.primary_employee_id ? [a.primary_employee_id] : []),
          ...a.assignee_employees.map((e) => e.id),
        ]),
      ];
    }

    const list = [...byLead.values()];
    if (hasOrphan) {
      list.push({
        key: 'Ohne Projekt',
        leadId: PLANTAFEL_ORPHAN_LEAD_ID,
        title: 'Ohne Projekt',
        customer: '—',
        trade: 'wartung',
        assigneeEmployeeIds: [...orphanAssignees],
      });
    }
    return list.sort((a, b) => {
      if (a.leadId === PLANTAFEL_ORPHAN_LEAD_ID) return 1;
      if (b.leadId === PLANTAFEL_ORPHAN_LEAD_ID) return -1;
      return a.customer.localeCompare(b.customer, 'de') || a.title.localeCompare(b.title, 'de');
    });
  }, [activities]);

  const periodLabel = React.useMemo(() => {
    if (viewMode === 'tag') {
      return new Intl.DateTimeFormat('de-DE', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(viewportStart);
    }
    if (viewMode === 'monat') {
      return new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(focusDate);
    }
    const firstDow = visibleWeekdays[0] ?? 0;
    const lastDow = visibleWeekdays[visibleWeekdays.length - 1] ?? 6;
    const weekStart = addDays(viewportStart, firstDow);
    const weekEnd = addDays(viewportStart, lastDow);
    return `${new Intl.DateTimeFormat('de-DE', { day: 'numeric' }).format(weekStart)}. – ${new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }).format(weekEnd)}`;
  }, [viewMode, viewportStart, focusDate, visibleWeekdays]);

  const kwLabel = React.useMemo(() => {
    if (viewMode === 'monat' && monthMeta) {
      const first = isoWeekNumber(monthMeta.viewportStart);
      const last = isoWeekNumber(addDays(monthMeta.viewportStart, monthMeta.dayCount - 1));
      return `· KW ${first} – ${last}`;
    }
    return `· KW ${isoWeekNumber(viewportStart)}`;
  }, [viewMode, viewportStart, monthMeta]);

  const navPrevLabel =
    viewMode === 'tag' ? 'Vorheriger Tag' : viewMode === 'monat' ? 'Vorheriger Monat' : 'Vorherige Woche';
  const navNextLabel =
    viewMode === 'tag' ? 'Nächster Tag' : viewMode === 'monat' ? 'Nächster Monat' : 'Nächste Woche';

  const mitarbeiterFilterEmployees = employees.map((m) => ({
    id: m.id,
    name: (m.name ?? '').trim() || 'Mitarbeiter',
    color_idx: m.color_idx,
    avatarColor: m.color,
    initials: m.initials,
    subtitle: (m.role ?? '').trim() || undefined,
  }));

  const typeFilterOptions = buildPlantafelTypeFilterOptions(
    activityTypes.length > 0 ? activityTypes : FALLBACK_ACTIVITY_TYPES,
  );
  const tooltipActivityTypes = React.useMemo(
    () => (activityTypes.length > 0 ? activityTypes : FALLBACK_ACTIVITY_TYPES),
    [activityTypes],
  );

  const leadById = React.useMemo(() => {
    const map = new Map<string, (typeof leads)[number]>();
    for (const lead of leads) map.set(lead.id, lead);
    return map;
  }, [leads]);

  const projektFilterOptions = projects.map((p) => {
    const T = TRADE_STYLES[p.trade];
    const lead = leadById.get(p.leadId);
    return {
      id: p.leadId,
      label: p.title,
      subtitle: (p.customer ?? '').trim() || undefined,
      searchText: lead ? leadSelectSearchText(lead) : undefined,
      leading: (
        <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: T.solid }} aria-hidden />
      ),
    };
  });

  const selectedActivity = drawerActivityId
    ? activities.find((a) => a.id === drawerActivityId) ?? null
    : null;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-app-surface">
      {conflictDialog}
      <div className="flex w-full shrink-0 flex-wrap items-center gap-3 border-b border-app-border bg-app-surface px-4 py-2.5">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
        <CalendarViewSwitcher instancePrefix={instancePrefix} active="calendar" />

        <div className="flex items-center overflow-hidden rounded-md border border-app-border bg-app-surface">
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center border-r border-app-border text-app-text-subtle hover:bg-app-hover"
            onClick={() => setFocusDate((d) => navigateFocusPrev(d, viewMode))}
            aria-label={navPrevLabel}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={`h-8 border-r border-app-border px-3 text-app-text hover:bg-app-hover ${TOOLBAR_TEXT}`}
            onClick={() => setFocusDate(new Date())}
          >
            Heute
          </button>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center text-app-text-subtle hover:bg-app-hover"
            onClick={() => setFocusDate((d) => navigateFocusNext(d, viewMode))}
            aria-label={navNextLabel}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className={`flex shrink-0 items-center gap-1.5 ${TOOLBAR_TEXT}`}>
          <span className="whitespace-nowrap text-app-text">{periodLabel}</span>
          <span className="whitespace-nowrap text-app-text-subtle">{kwLabel}</span>
        </div>
        </div>

        <PlantafelToolbarFilters
          employees={mitarbeiterFilterEmployees}
          projektOptions={projektFilterOptions}
          typOptions={typeFilterOptions}
          mitarbeiter={filters.mitarbeiter}
          projekt={filters.projekt}
          typ={filters.typ}
          status={filters.status}
          onMitarbeiterChange={(v) => setFilters((f) => ({ ...f, mitarbeiter: v }))}
          onProjektChange={(v) => setFilters((f) => ({ ...f, projekt: v }))}
          onTypChange={(v) => setFilters((f) => ({ ...f, typ: v }))}
          onStatusChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          onResetFilters={() =>
            setFilters(() => ({ mitarbeiter: [], projekt: [], typ: [], status: [] }))
          }
          typPanelHint="Zeigt nur Blöcke der gewählten Aktivitäts- und Abwesenheitstypen. Mitarbeiter ohne passende Einträge im Zeitraum werden ausgeblendet."
          statusPanelHint="Zeigt nur Aktivitäten mit den gewählten Status. Abwesenheiten bleiben sichtbar."
        >
          <div className="flex items-center gap-2">
            <span className={`hidden text-app-text-subtle lg:inline ${TOOLBAR_TEXT}`}>Gruppieren:</span>
            <div className={TOOLBAR_SEGMENT_WRAP}>
              {(
                [
                  { v: 'mitarbeiter' as const, l: 'Mitarbeiter' },
                  { v: 'projekt' as const, l: 'Projekte' },
                ] as const
              ).map((o) => (
                <button
                  key={o.v}
                  type="button"
                  className={`transition-colors ${TOOLBAR_SEGMENT_BTN} ${
                    groupBy === o.v ? 'bg-app-surface text-app-text shadow-sm' : 'text-app-text-subtle hover:text-app-text-muted'
                  }`}
                  onClick={() => setGroupBy(o.v)}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <div className={TOOLBAR_SEGMENT_WRAP}>
            {(
              [
                { v: 'tag' as const, l: 'Tag' },
                { v: 'woche' as const, l: 'Woche' },
                { v: 'monat' as const, l: 'Monat' },
              ] as const
            ).map((o) => (
              <button
                key={o.v}
                type="button"
                className={`transition-colors ${TOOLBAR_SEGMENT_BTN} ${
                  viewMode === o.v
                    ? 'bg-app-surface text-app-text shadow-sm'
                    : 'text-app-text-subtle hover:text-app-text-muted'
                }`}
                onClick={() => setViewMode(o.v)}
              >
                {o.l}
              </button>
            ))}
          </div>

          <PlantafelSettingsMenu
            instancePrefix={instancePrefix}
            displaySettings={displaySettings}
            onSaved={setDisplaySettings}
          />
          <button
            type="button"
            className="btn-pipedrive-green inline-flex !h-9 shrink-0 items-center gap-1.5 !rounded-md !px-3.5 text-sm font-semibold"
            onClick={() => {
              setAbsenceDrawer(null);
              setDrawerActivityId(null);
              setCreateDraft(null);
              setShowCreate(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Aktivität
          </button>
        </PlantafelToolbarFilters>
      </div>

      {error && (
        <div className="shrink-0 border-b border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      )}

      {!boardReady ? (
        <PageLoadingState />
      ) : (
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={setBoardScrollNode}
            className="app-scrollbar min-h-0 flex-1 overflow-auto overscroll-contain"
          >
            <PlantafelActivityTypesContext.Provider value={tooltipActivityTypes}>
            <PlantafelGridBody
              instancePrefix={instancePrefix}
              viewportStart={viewportStart}
              now={now}
              viewMode={viewMode}
              employees={employees}
              activities={activities}
              projects={projects}
              blocks={blocks}
              groupBy={groupBy}
              collapsed={collapsed}
              onToggleSection={(key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}
              selectedBlockId={selectedBlockId}
              onSelectBlock={(id) => {
                setSelectedBlockId(id);
                if (id?.startsWith('abs-')) {
                  const block = blocks.find((b) => b.id === id);
                  if (block) {
                    const draft = blockToAbsenceDraft(block, viewportStart, absences);
                    if (draft) {
                      setAbsenceDrawer(draft);
                      setDrawerActivityId(null);
                      setShowCreate(false);
                      setCreateDraft(null);
                    } else {
                      setAbsenceDrawer(null);
                    }
                  } else {
                    setAbsenceDrawer(null);
                  }
                  return;
                }
                setAbsenceDrawer(null);
                if (id) {
                  const block = blocks.find((b) => b.id === id);
                  if (block?.activityId) setDrawerActivityId(block.activityId);
                } else {
                  setDrawerActivityId(null);
                }
              }}
              onCreateRange={(draft) => {
                setAbsenceDrawer(null);
                setDrawerActivityId(null);
                setCreateDraft(draft);
                // Vorschau des vorherigen Formulars nicht kurz auf den neuen Entwurf legen.
                setCreatePreview(null);
                setShowCreate(true);
                setSelectedBlockId(PENDING_CREATE_BLOCK_ID);
              }}
              onActivityTimeChange={(activityId, due_at, end_at) => {
                setActivities((prev) =>
                  prev.map((a) => (a.id === activityId ? { ...a, due_at, end_at } : a)),
                );
                const patchTime = async (confirmConflicts: boolean) => {
                  try {
                    const res = await fetch(`/api/activities/${encodeURIComponent(activityId)}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        due_at,
                        end_at,
                        ...(confirmConflicts ? { confirm_conflicts: true } : {}),
                      }),
                    });
                    // Block stays at the new spot while the dialog is open; cancel snaps it back.
                    if (await interceptConflict(res, () => patchTime(true), () => void loadBoard())) return;
                    if (!res.ok) {
                      const b = await res.json().catch(() => ({}));
                      throw new Error((b as { error?: string }).error || 'Speichern fehlgeschlagen.');
                    }
                  } catch {
                    void loadBoard();
                  }
                };
                void patchTime(false);
              }}
              filters={filters}
              publicHolidayByDate={publicHolidayByDate}
              monthMeta={monthMeta}
              displaySettings={displaySettings}
              timeZone={timeZone}
            />
            </PlantafelActivityTypesContext.Provider>
          </div>
          <UtilizationFooter
            days={utilizationDays}
            viewMode={viewMode}
            viewportStart={viewportStart}
            monthMeta={monthMeta}
            visibleWeekdays={visibleWeekdays}
          />

          {absenceDrawer && (
            <PlantafelAbsenceDrawer
              draft={absenceDrawer}
              employees={employees}
              onClose={() => {
                setAbsenceDrawer(null);
                setSelectedBlockId(null);
              }}
              onSaved={() => {
                void loadBoard();
                setAbsenceDrawer(null);
                setSelectedBlockId(null);
              }}
            />
          )}

          {(drawerActivityId || showCreate) && !absenceDrawer && (
            <PlantafelDetailDrawer
              activity={showCreate ? null : selectedActivity}
              createDraft={showCreate ? createDraft : null}
              onCreatePreviewChange={setCreatePreview}
              leads={leads}
              activityTypes={activityTypes}
              instanceMembers={instanceMembers}
              employees={employees}
              onClose={() => {
                setDrawerActivityId(null);
                setShowCreate(false);
                setCreateDraft(null);
                setSelectedBlockId(null);
                setAbsenceDrawer(null);
              }}
              onSaved={() => {
                void loadBoard();
                if (showCreate) {
                  setShowCreate(false);
                  setDrawerActivityId(null);
                  setCreateDraft(null);
                  setSelectedBlockId(null);
                }
              }}
              onDeleted={() => {
                void loadBoard();
                setDrawerActivityId(null);
                setShowCreate(false);
                setCreateDraft(null);
              }}
              onLeadCreated={onLeadCreated}
              instancePrefix={instancePrefix}
            />
          )}
        </div>
      )}
    </div>
  );
}
